import Text from 'components/Text';
import Sidebar from 'components/layout/Sidebar';
import reportActions from 'features/report/services/actions';
import reportSelectors from 'features/report/services/selectors';
import { IParamsReportSalaryStaff } from 'features/report/services/types/review';
import { sumBy } from 'lodash';
import { Fragment, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import shopSelectors from 'services/shop/selectors';
import { useAppDispatch } from 'store/hooks';
import { formatCurrency } from 'utils/formatCurrency';
import PayrollReceiptWrapper from './PayrollReceiptWrapper';
import PrintHeader from './PrintHeader';
import StaffPayrollDetail from './StaffPayrollDetail';
import { ContainerStyled, DividerSlit, ReportPayrollCaption2HighLightRow, ReportPayrollCaption3Row } from './components';
import usePayrollTemplate from './templates';


const StaffPayrollPrint = () => {
  const divRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const params: IParamsReportSalaryStaff = useMemo(() => {
    const staffId: string = searchParams.get('staffId') || '';
    const endDate: string = searchParams.get('endDate') || '';
    const startDate: string = searchParams.get('startDate') || '';
    return ({
      staffId, endDate, startDate,
    });
  }, [searchParams]);

  useEffect(() => {
    dispatch(reportActions.getReportSalaryStaffV2.fetch({
      staffIds: params.staffId ? [params.staffId] : location.state?.staffIds || [],
      startDate: location.state?.startDate || params.startDate,
      endDate: location.state?.endDate || params.endDate,
    }));
  }, [params]);

  const onBack = () => navigate(-1);

  const onPrint = useReactToPrint({
    content: () => divRef.current,
    copyStyles: true,
  });

  return (
    <div style={{ display: 'flex' }}>
      <Sidebar />
      <ContainerStyled>
        <PrintHeader onBack={onBack} onPrint={onPrint} />
        <div ref={divRef}>
          <StaffPayrollPrintUI params={params} />
        </div>
      </ContainerStyled >
    </div >
  );
};

export default StaffPayrollPrint;
type Props = {
  params: IParamsReportSalaryStaff;
};
export const StaffPayrollPrintUI = ({ params }: Props) => {
  const allSetting = shopSelectors.data.allSetting();
  const staffPayrollData = reportSelectors.getStaffPayrollData();
  const cashLabel = allSetting?.labelSalaryCashOrBonus || 'Cash';
  const singleDate = params?.startDate == params?.endDate;
  const singleStaff = !!params.staffId;

  const template = usePayrollTemplate(params);

  const calculations = useMemo(() => {
    const totalSalary = sumBy(staffPayrollData, o => o.totalSalary || 0);
    const totalCash = sumBy(staffPayrollData, o => o.totalCash || 0);
    const totalCheck = sumBy(staffPayrollData, o => o.totalCheck || 0);
    return ({
      totalSalary,
      totalCash,
      totalCheck,
    });
  }, [staffPayrollData]);
  return (
    <PayrollReceiptWrapper params={params}>
      {!singleStaff && <>
        <ReportPayrollCaption2HighLightRow
          label='Employee Salaries'
          amount={formatCurrency(calculations?.totalSalary)}
        />
        <Text mb={0.25} />
        <ReportPayrollCaption3Row
          label={`Total ${cashLabel}`}
          amount={formatCurrency(calculations?.totalCash)}
        />
        <ReportPayrollCaption3Row
          label={'Total Check'}
          amount={formatCurrency(calculations?.totalCheck)}
        />
        <Text mb={0.5} />
      </>}
      {staffPayrollData.map((staff, i) => (
        <Fragment key={staff.staffId}>
          <StaffPayrollDetail
            key={staff.staffId}
            data={staff}
            cashLabel={allSetting?.labelSalaryCashOrBonus || 'Cash'}
            singleDate={singleDate}
            singleStaff={singleStaff}
            fields={template}
          />
          {((staffPayrollData?.length - 1) !== i) && <DividerSlit />}
        </Fragment>
      ))}
    </PayrollReceiptWrapper>
  );
};